import { Component } from '@angular/core';
import { ApiUrlHelper } from '../../../../common/api-url-helper';
import { CommonService } from '../../../../services/common/common.service';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { StateService } from '../../../../services/common/claim-year.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { EpwCostsByProject, commonLabelConstants } from '../../../../constants/label-constants';
import { environment } from '../../../../../environments/environment';
import { CustomCurrencyPipe } from '../../../../common/pipes/custom-currency.pipe';
import { SharedService } from '../../../../services/shared-service/shared.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ValidationService } from '../../../../services/common/validation-service';

@Component({
  selector: 'app-epw-costs-by-project-form',
  templateUrl: './epw-cost-by-project-form.component.html',
  styleUrl: './epw-cost-by-project-form.component.scss'
})

export class EPWCostsByProjectFormComponent {

  env: any = environment;
  projectList: any = [];
  companyId: any = 0
  claimYearId: any = 0;
  epwUnconnectedRNDForm!: FormGroup;
  detail: any = []
  isLoading: boolean = false;
  labelConstants = EpwCostsByProject;
  onDestroy$: Subject<void> = new Subject();
  commonLabelConstants = commonLabelConstants;
  isLocked: boolean = false;

  constructor(
    private readonly api: ApiUrlHelper,
    private readonly commonService: CommonService,
    private readonly sharedDataService: StateService,
    private readonly spinner: NgxSpinnerService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly currencyPipe : CustomCurrencyPipe,
    private readonly sharedService: SharedService,
    private readonly validationService:ValidationService
  ) {
  }

  get contractor(): FormArray {
    return this.epwUnconnectedRNDForm.get('contractor') as FormArray
  }

  ngOnInit() {
    this.sharedService.setData(this.labelConstants.EpwCostByProjectHeader);
    this.epwUnconnectedRNDForm = this.fb.group({
      contractor: this.fb.array([])
    });
    this.sharedDataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    this.sharedDataService.yearId$.pipe(takeUntil(this.onDestroy$)).subscribe((yearId) => {
      this.claimYearId = yearId ?? null;
    
      if (this.companyId && this.claimYearId) {
        this.resetForm();
        this.getProjectListCompanyWise(this.companyId, this.claimYearId)
      }
    });
  }

  resetForm() {
    this.epwUnconnectedRNDForm = this.fb.group({
      contractor: this.fb.array([])
    });
  }

  getProjectTotal(projectIndex: number): number {
    let total = 0;
    this.contractor.controls.forEach(contractorControl => {
      const projectsArray = contractorControl.get('projects') as FormArray;
      const rndEligibilityValue = projectsArray.at(projectIndex)?.get('rndEligibility')?.value;
      if (rndEligibilityValue) {
        total += parseFloat(rndEligibilityValue);
      }
    });
    return total;
  }

  getGrandTotal(): number {
    return this.contractor.controls.reduce((grandTotal, contractorControl, index) => {
      return grandTotal + this.getRndEligibilitySum(index);
    }, 0);
  }

  getProjectListCompanyWise(companyId: any, claimYearId: any) {
    this.spinner.show();
    let api = this.api.apiUrl.Project.GetProjectListWithOutPagination.replace('{companyId}', companyId).replace('{claimYearId}', claimYearId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.projectList = response.data;
        }
        else {
          this.projectList = [];
        }
        this.getContractorDetail();
        this.spinner.hide();
      },
      error: (error: any) => {
        console.log(error);
      }
    });
  }

  createContractor(): FormGroup {
    return this.fb.group({
      epwUnConnectedId: [null],
      epwUnConnectedResourceId: [null],
      contractorName: [''],
      jobRole: [''],
      projects: this.fb.array([]),
      paymentsMadeToTheStaffProviderForUKWork: [{ value: '', disabled: true }],
      paymentsMadeToTheStaffProviderForNonUKWork: [{ value: '', disabled: true }],
      totalEligibilityAmountForUKWork: [{ value: 0, disabled: true }],
      totalEligibilityAmountForNonUKWork: [{ value: 0, disabled: true }]
    });
  }

  createContractorProjectDetail() {
    return this.fb.group({
      epwUnConnectedEligibilityId: [null],
      epwUnConnectedResourceId: [null],
      projectId: [''],
      rndEligibility: [''],
    });
  }

  getItemAtIndex(index: number): FormGroup {
    return this.contractor.at(index) as FormGroup;
  }

  getProjectDetails(index: any) {
    return this.getItemAtIndex(index).get('projects') as FormArray;
  }

  getContractorDetail() {
    let obj = {
      companyId: this.companyId,
      claimYearId: this.claimYearId
    }
    let api = this.api.apiUrl.EPWUnconnectedCostResource.GetEPWUnconnectedCostResource;
    this.commonService.doPost(api, obj).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.detail = response.data;
           this.isLocked = ( this.detail[0]?.isLocked ?? false); 
          this.detail.forEach((item: any) => {
            const newContractor = this.createContractor();
            newContractor.patchValue({
              epwUnConnectedId: item.epwUnConnectedId,
              contractorName: item.contractorName,
              epwUnConnectedResourceId: item.epwUnConnectedResourceId,
              jobRole: item.jobRole,
              paymentsMadeToTheStaffProviderForUKWork: item.paymentsMadeToTheStaffProviderForUKWork,
              paymentsMadeToTheStaffProviderForNonUKWork: item.paymentsMadeToTheStaffProviderForNonUKWork,
              totalEligibilityAmountForUKWork: item.totalEligibilityAmountForUKWork,
              totalEligibilityAmountForNonUKWork: item.totalEligibilityAmountForNonUKWork
            });
            const contractorDetailsArray = newContractor.get('projects') as FormArray;
            this.projectList.forEach((itemDetail: any) => {
              const newProjectDetail = this.createContractorProjectDetail();
              const projectData = item.projects.find((projectDetail: any) => projectDetail.projectId == itemDetail.projectId);
              const rndEligibilityValue = projectData?.rndEligibility ?? '0';
              newProjectDetail.patchValue({
                epwUnConnectedEligibilityId: projectData ? projectData.staffCostResourceEligibilityId : 0,
                epwUnConnectedResourceId: projectData ? projectData.staffCostResourceId : 0,
                projectId: itemDetail.projectId,
                rndEligibility: rndEligibilityValue
              });
              newProjectDetail.disable();
              contractorDetailsArray.push(newProjectDetail);
            });

            this.contractor.push(newContractor);
          })
        }
      },
      error: (error: any) => {
        this.spinner.hide();
        console.log(error);
      }
    });
  }

  getRndEligibilitySum(contractorIndex: number): number {
    const projectDetails = this.getProjectDetails(contractorIndex).controls;
    let sum = 0;
    projectDetails.forEach((project: any) => {
      const rndEligibility = project.get('rndEligibility').value ? project.get('rndEligibility').value : 0;
      sum += rndEligibility ? parseFloat(rndEligibility) : 0;
    });
    return sum;
  }

  onSubmit() {
    const epwUnConnectedResourceRequest = {
      companyId: this.companyId,
      claimYearId: this.claimYearId,
      resourceModel: this.contractor.controls.map((contractorControl: any) => {
        return {
          epwUnConnectedResourceId: contractorControl.get('epwUnConnectedResourceId')?.value,
          epwUnConnectedId: contractorControl.get('epwUnConnectedId')?.value,
          projects: (contractorControl.get('projects') as FormArray).controls.map((projectControl: any) => {
            return {
              epwUnConnectedEligibilityId: projectControl.get('epwUnConnectedEligibilityId')?.value,
              epwUnConnectedResourceId: projectControl.get('epwUnConnectedResourceId')?.value,
              epwUnConnectedId: contractorControl.get('epwUnConnectedId')?.value,
              projectId: projectControl.get('projectId')?.value,
              rndEligibility: projectControl.get('rndEligibility')?.value ? parseFloat(projectControl.get('rndEligibility')?.value) : null
            };
          })
        };
      })
    };

    this.spinner.show();
    let api = this.api.apiUrl.EPWUnconnectedCostResource.SaveEPWUnconnectedCostResource;
    this.commonService.doPost(api, epwUnConnectedResourceRequest).pipe().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success) {
          this.toastr.success(response.message);
        }
        else {
          this.toastr.error(response.message);
        }
      },
      error: (error: any) => {
        console.log(error);
        this.spinner.hide();
      }
    });
  }

  goBack(): void {
    this.router.navigateByUrl('/dashboard');
  }

  getCostTotal(): number {
    return this.contractor.controls.reduce((totalCost, contractor) => {
      const cost = contractor.get('paymentsMadeToTheStaffProviderForUKWork')?.value || 0;
      return totalCost + Number(cost);
    }, 0);
  }

  getCostTotal1(): number {
    return this.contractor.controls.reduce((totalCost, contractor) => {
      const cost = contractor.get('paymentsMadeToTheStaffProviderForNonUKWork')?.value || 0;
      return totalCost + Number(cost);
    }, 0);
  }

  getTotalProduct(projectIndex: number): string | null {
    let totalProduct = 0;
    for (let i = 0; i < this.contractor.length; i++) {
      const employee = this.getItemAtIndex(i);
      const projectDetails = this.getProjectDetails(i);

      if (projectIndex < projectDetails.length) {
        const totalGrossStr = employee.get('paymentsMadeToTheStaffProviderForUKWork')?.value;
        const totalGrossStrNONUK = employee.get('paymentsMadeToTheStaffProviderForNonUKWork')?.value;
        const totalGross = parseFloat(totalGrossStr) || 0;
        const totalGrossNONUK = parseFloat(totalGrossStrNONUK) || 0;

        const rndEligibilityValueStr = projectDetails.at(projectIndex).get('rndEligibility')?.value;
        const rndEligibility = (parseFloat(rndEligibilityValueStr) || 0) / 100;

        totalProduct += (totalGross + totalGrossNONUK) * rndEligibility;
      } else {
        console.warn(`Project index ${projectIndex} is out of bounds for employee ${i}.`);
      }
    }
    return this.currencyPipe.transform(totalProduct === 0 ? 0 : Number((totalProduct * 0.65).toFixed(2)) , environment.CurrencyFormat);
  }

  getTotalQualifyingCosts(): number {
    let total = 0;
    const employeeArray = this.epwUnconnectedRNDForm.get('contractor') as FormArray;

    employeeArray.controls.forEach((employee) => {
      total += employee.get('totalEligibilityAmountForUKWork')?.value || 0;
    });
    return total;
  }

  getTotalQualifyingCosts1(): number {
    let total = 0;
    const employeeArray = this.epwUnconnectedRNDForm.get('contractor') as FormArray;

    employeeArray.controls.forEach((employee) => {
      total += employee.get('totalEligibilityAmountForNonUKWork')?.value || 0;
    });
    return total;
  }
  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  exportList(): void {
    const isValid = this.validationService.validateCompanyAndYear(
      this.companyId,
      this.claimYearId
    );
    if (!isValid) {
      return;
    }
    const companyIdStr = this.companyId !== null ? String(this.companyId) : '';
    const yearIdStr = this.claimYearId !== null ? String(this.claimYearId) : '';
    let obj = {
      CompanyId: companyIdStr,
      ClaimYearId: yearIdStr,
    };
    let api = this.api.apiUrl.EPWUnconnectedCostResource.ExportExpenditureCategoryCost;
    this.commonService.doDownloadPost(api, obj).subscribe(
      (response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = "Externally Provided Workers - Unconnected - Qualifying Expenditure by Cost Category.xlsx";

        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }
}